import { useEffect, useState } from "react";

import { adminApi, type Health } from "@/api/admin";
import { ApiError } from "@/api/client";

const REFRESH_MS = 30_000;

export default function HealthPage() {
  const [data, setData] = useState<Health | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);

  useEffect(() => {
    let cancelled = false;
    const tick = () => {
      adminApi
        .health()
        .then((r) => {
          if (cancelled) return;
          setData(r);
          setRefreshedAt(new Date());
          setError(null);
        })
        .catch((e: unknown) => {
          if (cancelled) return;
          setError(e instanceof ApiError ? e.message : String(e));
        });
    };
    tick();
    const id = setInterval(tick, REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  if (error)
    return (
      <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
        {error}
      </div>
    );
  if (!data)
    return <div className="py-6 text-sm text-slate-500">加载中…</div>;

  return (
    <div className="space-y-4">
      <div className="text-xs text-slate-400">
        每 {REFRESH_MS / 1000}s 自动刷新 · 上次：
        {refreshedAt ? refreshedAt.toLocaleTimeString("zh-CN") : "—"}
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        <StatusCard
          label="数据库"
          ok={data.db_ok}
          detail={data.db_error || (data.db_ok ? "连接正常" : undefined)}
        />
        <StatusCard
          label="飞书 WebSocket"
          ok={data.ws_connected}
          detail={data.ws_connected ? "已连接" : "未连接"}
        />
        <StatusCard
          label="运行时长"
          ok={true}
          detail={formatUptime(data.uptime_seconds)}
        />
      </div>

      <section className="rounded-xl border border-slate-200 bg-white p-4">
        <h3 className="mb-3 text-sm font-semibold">数据库连接池</h3>
        <div className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
          <Counter label="size" value={data.pool.size ?? "—"} />
          <Counter label="checked_in" value={data.pool.checked_in ?? "—"} />
          <Counter label="checked_out" value={data.pool.checked_out ?? "—"} />
          <Counter label="overflow" value={data.pool.overflow ?? "—"} />
        </div>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-4">
        <h3 className="mb-3 text-sm font-semibold">
          1h 飞书调用 (总{data.feishu_calls_total_1h}, 错误率
          {(data.feishu_error_rate_1h * 100).toFixed(1)}%)
        </h3>
        <table className="w-full text-left text-sm">
          <thead className="text-xs uppercase text-slate-500">
            <tr>
              <th className="px-2 py-1">API</th>
              <th className="px-2 py-1 text-right">成功</th>
              <th className="px-2 py-1 text-right">失败</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(data.feishu_calls_last_1h).map(([api, c]) => (
              <tr key={api} className="border-t border-slate-100">
                <td className="px-2 py-1 font-mono text-xs">{api}</td>
                <td className="px-2 py-1 text-right tabular-nums">{c.ok ?? 0}</td>
                <td className="px-2 py-1 text-right tabular-nums text-rose-700">
                  {c.err ?? 0}
                </td>
              </tr>
            ))}
            {Object.keys(data.feishu_calls_last_1h).length === 0 ? (
              <tr>
                <td colSpan={3} className="px-2 py-3 text-center text-slate-400">
                  最近 1h 无调用
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-4">
        <h3 className="mb-3 text-sm font-semibold">1h 业务事件</h3>
        <div className="flex flex-wrap gap-2 text-sm">
          {Object.entries(data.events_last_1h).map(([k, v]) => (
            <span
              key={k}
              className="rounded-full bg-slate-100 px-3 py-1 text-xs"
            >
              {k}: <span className="font-semibold tabular-nums">{v}</span>
            </span>
          ))}
          {Object.keys(data.events_last_1h).length === 0 ? (
            <span className="text-xs text-slate-400">无</span>
          ) : null}
        </div>
      </section>
    </div>
  );
}

function StatusCard({
  label,
  ok,
  detail,
}: {
  label: string;
  ok: boolean;
  detail?: string;
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="mt-1 flex items-center gap-2">
        <span
          className={`h-2 w-2 rounded-full ${ok ? "bg-emerald-500" : "bg-rose-500"}`}
        />
        <span className="text-sm font-medium">
          {ok ? "正常" : "异常"}
        </span>
      </div>
      {detail ? (
        <div className="mt-1 text-xs text-slate-500">{detail}</div>
      ) : null}
    </div>
  );
}

function Counter({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-lg bg-slate-50 p-3">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="mt-1 text-xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function formatUptime(sec: number): string {
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}
